/**
 * Rustic Edge — Defensive cart-add patch v2
 *
 * v2 changes (2026-05-11 evening, after Edge customer failure showed v1 wasn't catching):
 *   1. DUAL LOGGING — every failure logs to TWO places:
 *        a) Same-origin: writes to cart.attributes via /cart/update.js
 *           (cannot be blocked by Edge tracking prevention — same domain as the store)
 *        b) Remote: existing Google Apps Script webhook (kept as backup)
 *      The cart-attribute log is then rendered as an HTML comment on the cart page
 *      so we can read it via View Source even if the customer can't share DevTools.
 *
 *   2. GLOBAL ERROR HANDLER — window.addEventListener('error') + 'unhandledrejection'
 *      catch ANY uncaught error on the page, even ones thrown outside product-form.js
 *      (constructor errors, capture-phase listeners, third-party app scripts, etc.).
 *      If the spinner is showing when a global error fires, we force a native form
 *      submit as a last-ditch save.
 *
 *   3. RESPONSE-HANDLER TRY/CATCH — the .then() chain handling Shopify's response
 *      (including cart.renderContents) is now wrapped. If rendering throws, we log
 *      and navigate to /cart so the customer at least sees their item.
 *
 *   4. PRE-FETCH TRY/CATCH — unchanged from v1.
 *
 * Nothing on the happy path is changed.
 */

const CART_LOG_URL = 'https://script.google.com/macros/s/AKfycbzPdyapLRhr48zuu53a7tkwhqV3VrrR9dkn7n3DX-6wrC_gKBsytzQJy8NVElMQktT4/exec';
const CART_LOG_SECRET = 'rusticedge-cart-log-v1';

// --- Logging ---------------------------------------------------------------

function logCartFailureSameOrigin(payload) {
  // Same-origin log: writes to cart.attributes. Cannot be blocked by tracking
  // prevention because /cart/update.js lives on the storefront's own domain.
  // The cart template renders these attributes as an HTML comment on /cart.
  try {
    const data = JSON.stringify(payload).slice(0, 1800);
    const fd = new FormData();
    fd.append('attributes[__re_error_log]', data);
    fd.append('attributes[__re_error_timestamp]', new Date().toISOString());
    fetch('/cart/update.js', {
      method: 'POST',
      body: fd,
      keepalive: true,
    }).catch(function () { /* swallow */ });
  } catch (_) { /* logging must never throw */ }
}

function logCartFailureRemote(payload) {
  try {
    if (!CART_LOG_URL || CART_LOG_URL.indexOf('PASTE_') === 0) return;
    const body = JSON.stringify(Object.assign({ secret: CART_LOG_SECRET }, payload));
    if (navigator.sendBeacon) {
      navigator.sendBeacon(CART_LOG_URL, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
    } else {
      fetch(CART_LOG_URL, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
    }
  } catch (_) { /* logging must never throw */ }
}

// Phases that represent a real cart-add failure (vs. unrelated page-load JS noise).
// Only these write to cart.attributes; otherwise we pollute every order's
// "Additional details" with iOS/Android in-app WebView errors that have nothing
// to do with checkout.
const CART_PHASES = [
  'pre-fetch-throw',
  'response-error',
  'fetch-catch',
  'response-handler-throw',
  'fallback-submit-throw',
  'constructor-throw',
];

function logCartFailure(payload) {
  if (CART_PHASES.indexOf(payload.phase) !== -1) {
    logCartFailureSameOrigin(payload);
  }
  logCartFailureRemote(payload);
}

// --- Global safety net -----------------------------------------------------

// Third-party noise we don't want polluting the cart-failure log. These are
// errors thrown by Shopify-owned, app-owned, or cross-origin code that have
// no link to add-to-cart. Cart-phase errors (pre-fetch-throw, response-error,
// fetch-catch) bypass this filter because they log directly, not via these
// global handlers.
function isThirdPartyNoise(message, stack) {
  const m = String(message || '');
  const s = String(stack || '');

  // Cross-origin opaque error — browser redacted the details, undiagnosable.
  if (m === 'Script error.' || m === 'Script error') return true;

  // Shopify-owned first-party libs (shop-js telemetry, web pixels, storefront chunks).
  if (/shopifycloud\/shop-js/.test(s)) return true;
  if (/shopifycloud\/storefront/.test(s)) return true;
  if (/\/web-pixels\//.test(s) || /\/web-pixels\//.test(m)) return true;
  if (/web-pixel-\d+@/.test(m)) return true;
  if (/recordCounter outside of context/.test(m)) return true;

  // Shopify app extensions (Globo swatch chunk loads, etc.).
  if (/cdn\.shopify\.com\/extensions\//.test(s)) return true;

  // Known third-party app endpoints whose errors are not cart-related.
  if (/alia-prod\.com/.test(s)) return true;

  // iOS in-app WebView (Instagram/Facebook) native-bridge errors. These fire on
  // page load because the page expects browser context but is running in an
  // embedded WebView with restricted/missing native handlers.
  if (/_AutofillCallbackHandler/.test(m)) return true;
  if (/window\.webkit\.messageHandlers/.test(m)) return true;

  // Facebook Android in-app browser instrumentation (iabjs:// scheme + the
  // navigation_performance_logger native bridge dying on page nav).
  if (/iabjs:\/\//.test(s)) return true;
  if (/enableDidUserTypeOnKeyboardLogging/.test(m)) return true;
  if (/Java object is gone/.test(m)) return true;

  // Minified third-party (FB/IG in-app) — pattern "x[r(NNN)] is not a function"
  // with fully opaque cross-origin stacks. Distinctive enough to filter safely.
  if (/is not a function\. \(In 'x\[r\(\d+\)\]\(\)'/.test(m)) return true;

  return false;
}

window.addEventListener('error', function (e) {
  try {
    const msg = (e.error && e.error.message) || e.message || 'unknown';
    const stack = e.error && e.error.stack;

    if (!isThirdPartyNoise(msg, stack)) {
      logCartFailure({
        error: msg,
        stack: stack,
        phase: 'window-onerror',
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
    }

    // If a product-form is showing its loading spinner when this global error
    // fires, the customer is mid-checkout. Force a native form submit so they
    // still land on /cart with the item. Runs even for filtered noise — the
    // gate is the spinner being active, not what the error was.
    const productForm = document.querySelector('product-form form');
    const loadingBtn = document.querySelector('product-form .product-form__submit.loading, product-form button[aria-disabled="true"]');
    if (productForm && loadingBtn) {
      try {
        const idInput = productForm.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        HTMLFormElement.prototype.submit.call(productForm);
      } catch (_) {}
    }
  } catch (_) {}
});

window.addEventListener('unhandledrejection', function (e) {
  try {
    const reason = e.reason || {};
    const msg = reason.message || String(reason);
    const stack = reason.stack;

    if (isThirdPartyNoise(msg, stack)) return;

    logCartFailure({
      error: msg,
      stack: stack,
      phase: 'unhandled-rejection',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  } catch (_) {}
});

// --- ProductForm custom element --------------------------------------------

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        try {
          this.form = this.querySelector('form');
          this.variantIdInput.disabled = false;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.submitButton = this.querySelector('[type="submit"]');
          this.submitButtonText = this.submitButton.querySelector('span');

          if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

          this.hideErrors = this.dataset.hideErrors === 'true';
        } catch (err) {
          // Constructor errors normally make the element silently dead.
          // Log it so we can see what broke.
          logCartFailure({
            error: err && err.message,
            stack: err && err.stack,
            phase: 'constructor-throw',
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          });
          throw err;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // === DEFENSIVE WRAPPER (pre-fetch) ===
        let config, formData;
        try {
          this.handleErrorMessage();

          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');

          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        } catch (err) {
          logCartFailure({
            error: err && err.message,
            stack: err && err.stack,
            phase: 'pre-fetch-throw',
            url: window.location.href,
            userAgent: navigator.userAgent,
            variantId: this.variantIdInput && this.variantIdInput.value,
            timestamp: new Date().toISOString(),
          });
          this.fallbackToNativeSubmit();
          return;
        }
        // === END pre-fetch wrapper ===

        const self = this;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // === DEFENSIVE WRAPPER (response handler) ===
            try {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                self.handleErrorMessage(response.description);

                logCartFailure({
                  error: 'cart-add returned status: ' + response.status + ' / ' + (response.description || response.message || ''),
                  phase: 'response-error',
                  url: window.location.href,
                  userAgent: navigator.userAgent,
                  variantId: formData.get('id'),
                  timestamp: new Date().toISOString(),
                });

                const soldOutMessage = self.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                self.submitButton.setAttribute('aria-disabled', true);
                self.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                self.error = true;
                return;
              } else if (!self.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!self.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              self.error = false;
              const quickAddModal = self.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        self.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  self.cart.renderContents(response);
                });
              }
            } catch (renderErr) {
              // Response came back OK from Shopify (item IS in the cart server-side),
              // but rendering the drawer threw. Log it and send the customer to /cart
              // so they see their item.
              logCartFailure({
                error: renderErr && renderErr.message,
                stack: renderErr && renderErr.stack,
                phase: 'response-handler-throw',
                url: window.location.href,
                userAgent: navigator.userAgent,
                variantId: formData && formData.get('id'),
                timestamp: new Date().toISOString(),
              });
              window.location = window.routes.cart_url;
            }
            // === END response handler wrapper ===
          })
          .catch((e) => {
            console.error(e);
            logCartFailure({
              error: e && e.message,
              stack: e && e.stack,
              phase: 'fetch-catch',
              url: window.location.href,
              userAgent: navigator.userAgent,
              variantId: formData && formData.get('id'),
              timestamp: new Date().toISOString(),
            });
            // Network failure on the AJAX call. Try the native submit as last resort.
            try { self.fallbackToNativeSubmit(); } catch (_) {}
          })
          .finally(() => {
            try {
              self.submitButton.classList.remove('loading');
              if (self.cart && self.cart.classList.contains('is-empty')) self.cart.classList.remove('is-empty');
              if (!self.error) self.submitButton.removeAttribute('aria-disabled');
              const spinner = self.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');

              CartPerformance.measureFromEvent("add:user-action", evt);
            } catch (_) {}
          });
      }

      /**
       * Native submit fallback. The Liquid {% form 'product' %} tag emits
       * <form action="/cart/add" method="post">, so calling HTMLFormElement.prototype.submit
       * posts to /cart/add and the browser navigates to /cart with the item added.
       * HTMLFormElement.submit() does NOT fire the submit event, so no infinite loop.
       */
      fallbackToNativeSubmit() {
        try {
          HTMLFormElement.prototype.submit.call(this.form);
        } catch (err) {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          logCartFailure({
            error: err && err.message,
            stack: err && err.stack,
            phase: 'fallback-submit-throw',
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          });
          throw err;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}